
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { Sentry, isSentryEnabled } from '@/sentry';

type Profile = Tables<'profiles'>;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const loadedProfileUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    
    
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);

        if (isSentryEnabled) {
          if (session?.user) {
            Sentry.setUser({ id: session.user.id, email: session.user.email ?? undefined });
          } else {
            Sentry.setUser(null);
          }
        }

        // Set auth loading to false immediately when we have session data
        if (mounted) {
          setLoading(false);
        }

        if (!session?.user) {
          // User signed out – clear the profile
          setProfile(null);
          setProfileLoading(false);
          loadedProfileUserIdRef.current = null;
          return;
        }

        const userId = session.user.id;
        const hasProfileForCurrentUser =
          loadedProfileUserIdRef.current === userId;
        const shouldBlockWhileLoadingProfile = !hasProfileForCurrentUser;

        // Supabase can emit auth events (for example SIGNED_IN/TOKEN_REFRESHED)
        // when browser tab focus changes. Avoid toggling profileLoading for a
        // user whose profile is already loaded to prevent full page remounts
        // that clear in-progress forms.
        if (event === 'TOKEN_REFRESHED' && hasProfileForCurrentUser) {
          return;
        }
        setProfileLoading(shouldBlockWhileLoadingProfile);

        // Use setTimeout to avoid blocking the auth state change
        setTimeout(async () => {
          if (!mounted) return;
          
          try {
            const { data: profileData, error } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single();
            
            if (mounted) {
              if (error) {
                console.error('AuthProvider: Error fetching profile:', error);
                if (isSentryEnabled) Sentry.captureException(error);
                setProfile(null);
                loadedProfileUserIdRef.current = null;
              } else {
                setProfile(profileData);
                loadedProfileUserIdRef.current = userId;
              }
              setProfileLoading(false);
            }
          } catch (error) {
            console.error('AuthProvider: Exception fetching profile:', error);
            if (isSentryEnabled) Sentry.captureException(error);
            if (mounted) {
              setProfile(null);
              loadedProfileUserIdRef.current = null;
              setProfileLoading(false);
            }
          }
        }, 0);
      }
    );

    // Check for existing session
    
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mounted) return;
      
      
      
      if (error) {
        console.error('AuthProvider: Error getting session:', error);
        if (isSentryEnabled) Sentry.captureException(error);
        setLoading(false);
        return;
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      
      // Set loading to false immediately after checking session
      setLoading(false);
    });

    return () => {
      mounted = false;
      
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        console.error('AuthProvider: Sign in error:', error);
      }
      return { error };
    } catch (error) {
      console.error('AuthProvider: Sign in exception:', error);
      if (isSentryEnabled) Sentry.captureException(error);
      return { error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    
    try {
      const redirectUrl = `${window.location.origin}/`;
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          },
        },
      });
      if (error) {
        console.error('AuthProvider: Sign up error:', error);
      }
      return { error };
    } catch (error) {
      console.error('AuthProvider: Sign up exception:', error);
      if (isSentryEnabled) Sentry.captureException(error);
      return { error };
    }
  };

  const signOut = async () => {
    
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('AuthProvider: Sign out error:', error);
      if (isSentryEnabled) Sentry.captureException(error);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      profileLoading,
      signIn,
      signUp,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
